/**
 * React 接线（T10）：把 `VoiceController` 包装成可订阅的 hook。
 *
 * - 控制器按注入依赖的「实例身份」只在必要时重建（fake 适配器引用稳定，不会反复重建）；
 * - 通过 `useSyncExternalStore` 订阅快照，任何状态变化都会触发使用方重渲染；
 * - `onTranscript` / `onIssue` 用 ref 兜底，避免它们变化时重建控制器。
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { VoiceController } from './controller';
import type { VoiceControllerOptions, VoiceSnapshot } from './controller';
import type { VoiceContextValue } from './context';

export type UseVoiceControllerOptions = VoiceControllerOptions;

export function useVoiceController(options: UseVoiceControllerOptions): VoiceContextValue {
  const onTranscriptRef = useRef(options.onTranscript);
  onTranscriptRef.current = options.onTranscript;
  const onIssueRef = useRef(options.onIssue);
  onIssueRef.current = options.onIssue;

  const { tts, transcribe, recorder, audioFactory, objectUrl } = options.deps;
  const storage = options.storage;

  const controller = useMemo(
    () =>
      new VoiceController({
        deps: { tts, transcribe, recorder, audioFactory, objectUrl },
        ...(storage === undefined ? {} : { storage }),
        onTranscript: (text: string) => {
          onTranscriptRef.current?.(text);
        },
        onIssue: (issue) => {
          onIssueRef.current?.(issue);
        },
      }),
    [tts, transcribe, recorder, audioFactory, objectUrl, storage],
  );

  const [snapshot, setSnapshot] = useState<VoiceSnapshot>(() => controller.getSnapshot());

  useEffect(() => {
    setSnapshot(controller.getSnapshot());
    const unsubscribe = controller.subscribe(() => {
      setSnapshot(controller.getSnapshot());
    });
    return () => {
      unsubscribe();
      controller.dispose();
    };
  }, [controller]);

  return useMemo(() => ({ controller, snapshot }), [controller, snapshot]);
}